import { AccountType } from "@common/constants/domains/accounting/accounts";
import { isPrepaidExpenseSubMode } from "@common/helpers/domains/accounting/expenseSubMode";
import {
    ExpenseSubMode,
    RevenueSubMode,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";

export interface ScheduleAccountMode {
    type: ScheduleType;
    revenueSubMode: RevenueSubMode | null;
    expenseSubMode: ExpenseSubMode | null;
}

const LIABILITY_HOLDING_ACCOUNT_TYPES: readonly AccountType[] = [
    AccountType.otherCurrentLiabilities,
    AccountType.otherLiabilities,
];

const ACCRUED_REVENUE_HOLDING_ACCOUNT_TYPES: readonly AccountType[] = [
    AccountType.accountsReceivable,
    AccountType.otherCurrentAssets,
    AccountType.otherAssets,
];

const PREPAID_EXPENSE_HOLDING_ACCOUNT_TYPES: readonly AccountType[] = [
    AccountType.otherCurrentAssets,
    AccountType.otherAssets,
];

const REVENUE_RECOGNITION_ACCOUNT_TYPES: readonly AccountType[] = [
    AccountType.income,
    AccountType.otherIncome,
];

const EXPENSE_RECOGNITION_ACCOUNT_TYPES: readonly AccountType[] = [
    AccountType.cogs,
    AccountType.operatingExpenses,
    AccountType.otherExpenses,
];

function getRevenueHoldingAccountTypes(
    revenueSubMode: RevenueSubMode | null,
): readonly AccountType[] {
    switch (revenueSubMode) {
        case RevenueSubMode.deferred:
            return LIABILITY_HOLDING_ACCOUNT_TYPES;
        case RevenueSubMode.accrued:
            return ACCRUED_REVENUE_HOLDING_ACCOUNT_TYPES;
        case null:
            return [];
    }
}

function getExpenseHoldingAccountTypes(
    expenseSubMode: ExpenseSubMode | null,
): readonly AccountType[] {
    if (isPrepaidExpenseSubMode(expenseSubMode)) {
        return PREPAID_EXPENSE_HOLDING_ACCOUNT_TYPES;
    }
    return expenseSubMode === ExpenseSubMode.accrued
        ? LIABILITY_HOLDING_ACCOUNT_TYPES
        : [];
}

/** The holding account types a sub-mode can use; none until one is picked. */
export function getScheduleHoldingAccountTypes(
    mode: ScheduleAccountMode,
): readonly AccountType[] {
    switch (mode.type) {
        case ScheduleType.revenue:
            return getRevenueHoldingAccountTypes(mode.revenueSubMode);
        case ScheduleType.expense:
            return getExpenseHoldingAccountTypes(mode.expenseSubMode);
        case ScheduleType.fixedAsset:
            return [];
    }
}

export function isScheduleHoldingAccountType(options: {
    accountType: AccountType;
    mode: ScheduleAccountMode;
}): boolean {
    return getScheduleHoldingAccountTypes(options.mode).some(
        (holdingAccountType) => holdingAccountType === options.accountType,
    );
}

export function getScheduleRecognitionAccountTypes(
    scheduleType: ScheduleType,
): readonly AccountType[] {
    switch (scheduleType) {
        case ScheduleType.revenue:
            return REVENUE_RECOGNITION_ACCOUNT_TYPES;
        case ScheduleType.expense:
            return EXPENSE_RECOGNITION_ACCOUNT_TYPES;
        case ScheduleType.fixedAsset:
            return [];
    }
}

export function isScheduleRecognitionAccountType(options: {
    accountType: AccountType;
    scheduleType: ScheduleType;
}): boolean {
    return getScheduleRecognitionAccountTypes(options.scheduleType).some(
        (recognitionAccountType) =>
            recognitionAccountType === options.accountType,
    );
}

/**
 * A stop can move the remaining balance out of the holding account, but not
 * into another holding account. Without a known sub-mode every holding set
 * of the schedule type is excluded.
 */
export function getScheduleReclassExcludedAccountTypes(
    mode: ScheduleAccountMode,
): readonly AccountType[] {
    const holdingAccountTypes = getScheduleHoldingAccountTypes(mode);
    if (holdingAccountTypes.length > 0) {
        return holdingAccountTypes;
    }
    switch (mode.type) {
        case ScheduleType.revenue:
            return [
                ...LIABILITY_HOLDING_ACCOUNT_TYPES,
                ...ACCRUED_REVENUE_HOLDING_ACCOUNT_TYPES,
            ];
        case ScheduleType.expense:
            return [
                ...PREPAID_EXPENSE_HOLDING_ACCOUNT_TYPES,
                ...LIABILITY_HOLDING_ACCOUNT_TYPES,
            ];
        case ScheduleType.fixedAsset:
            return [];
    }
}

export function isScheduleReclassTargetAccountType(options: {
    accountType: AccountType;
    mode: ScheduleAccountMode;
}): boolean {
    return !getScheduleReclassExcludedAccountTypes(options.mode).some(
        (excludedType) => excludedType === options.accountType,
    );
}
